/**
 * Centralized API Client for backend communication
 * Handles authentication, error handling, retry logic, and request/response interceptors
 */

import { API_BASE_URL } from '../config';

export class ApiError extends Error {
  constructor(
    public statusCode: number,
    public message: string,
    public data?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestConfig extends RequestInit {
  skipAuth?: boolean;
  /** Number of retry attempts for transient errors (502, 503, 504). Default: 3 */
  retries?: number;
  /** Initial delay in ms before first retry. Default: 1000 */
  retryDelay?: number;
  /** Request timeout in ms. Default: 30000 */
  timeout?: number;
}

/** HTTP status codes that indicate transient errors worth retrying */
const RETRYABLE_STATUS_CODES = [502, 503, 504];

/** Default retry configuration */
const DEFAULT_RETRIES = 5;
const DEFAULT_RETRY_DELAY = 1000;
const DEFAULT_TIMEOUT = 30000;

/**
 * Sleep utility for retry delays
 */
const sleep = (ms: number): Promise<void> => 
  new Promise(resolve => setTimeout(resolve, ms));

/**
 * Calculate exponential backoff delay with jitter
 */
const getBackoffDelay = (attempt: number, baseDelay: number): number => {
  const exponentialDelay = baseDelay * Math.pow(2, attempt);
  // Add 10-30% jitter to prevent thundering herd
  const jitter = exponentialDelay * (0.1 + Math.random() * 0.2);
  return Math.min(exponentialDelay + jitter, 30000); // Cap at 30 seconds
};

class ApiClient {
  private baseURL: string;
  private inflightGets = new Map<string, Promise<any>>();

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  private getAuthToken(): string | null {
    const user = localStorage.getItem('user');
    if (!user) return null;
    
    try {
      const userData = JSON.parse(user);
      return userData.idToken || null;
    } catch {
      return null;
    }
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (response.ok) {
      // Handle different content types
      const contentType = response.headers.get('content-type');
      
      if (contentType?.includes('application/json')) {
        return response.json();
      } else if (contentType?.includes('text/')) {
        return response.text() as unknown as T;
      } else {
        return response.blob() as unknown as T;
      }
    }

    // Handle errors
    let errorData: any = {};
    const contentType = response.headers.get('content-type');
    
    if (contentType?.includes('application/json')) {
      try {
        errorData = await response.json();
      } catch {
        // If JSON parsing fails, use status text
        errorData = { message: response.statusText };
      }
    } else {
      errorData = { message: response.statusText };
    }

    throw new ApiError(
      response.status,
      errorData.message || `Request failed with status ${response.status}`,
      errorData
    );
  }

  /**
   * Fetch with timeout support
   */
  private async fetchWithTimeout(
    url: string,
    options: RequestInit,
    timeout: number
  ): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      return response;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async request<T>(
    endpoint: string,
    config: RequestConfig = {}
  ): Promise<T> {
    const { 
      skipAuth, 
      retries = DEFAULT_RETRIES, 
      retryDelay = DEFAULT_RETRY_DELAY,
      timeout = DEFAULT_TIMEOUT,
      ...fetchConfig 
    } = config;
    
    const headers: Record<string, string> = {
      ...(fetchConfig.headers as Record<string, string> || {}),
    };

    // Add authorization header if not skipped and token exists
    if (!skipAuth) {
      const token = this.getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      } else if (!config.method || config.method === 'GET') {
        // Only throw auth error for authenticated endpoints
        // Some endpoints might be public
        console.warn('No authentication token found');
      }
    }

    // Add Content-Type for JSON requests unless it's FormData
    if (fetchConfig.body && !(fetchConfig.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    const url = `${this.baseURL}${endpoint}`;
    let lastError: Error | null = null;
    
    // Attempt request with retries for transient errors
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const response = await this.fetchWithTimeout(
          url,
          { ...fetchConfig, headers },
          timeout
        );

        // Check if this is a retryable error
        if (RETRYABLE_STATUS_CODES.includes(response.status) && attempt < retries) {
          const delay = getBackoffDelay(attempt, retryDelay);
          console.warn(
            `Request to ${endpoint} failed with ${response.status}, ` +
            `retrying in ${Math.round(delay)}ms (attempt ${attempt + 1}/${retries})`
          );
          await sleep(delay);
          continue;
        }

        return this.handleResponse<T>(response);
      } catch (error) {
        // Handle abort (timeout) errors
        if (error instanceof Error && error.name === 'AbortError') {
          lastError = new ApiError(0, `Request timeout after ${timeout}ms`, error);
          if (attempt < retries) {
            const delay = getBackoffDelay(attempt, retryDelay);
            console.warn(
              `Request to ${endpoint} timed out, ` +
              `retrying in ${Math.round(delay)}ms (attempt ${attempt + 1}/${retries})`
            );
            await sleep(delay);
            continue;
          }
        } else if (error instanceof ApiError) {
          throw error;
        } else {
          lastError = new ApiError(
            0,
            error instanceof Error ? error.message : 'Network error occurred',
            error
          );
          
          // Retry on network errors (could be cold start related)
          if (attempt < retries) {
            const delay = getBackoffDelay(attempt, retryDelay);
            console.warn(
              `Network error for ${endpoint}, ` +
              `retrying in ${Math.round(delay)}ms (attempt ${attempt + 1}/${retries})`
            );
            await sleep(delay);
            continue;
          }
        }
      }
    }

    // All retries exhausted
    throw lastError || new ApiError(0, 'Request failed after all retries');
  }

  async get<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    const existing = this.inflightGets.get(endpoint);
    if (existing) return existing as Promise<T>;

    const promise = this.request<T>(endpoint, {
      ...config,
      method: 'GET',
    }).finally(() => {
      this.inflightGets.delete(endpoint);
    });

    this.inflightGets.set(endpoint, promise);
    return promise;
  }

  async post<T>(
    endpoint: string,
    data?: any,
    config?: RequestConfig
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'POST',
      body: data instanceof FormData ? data : JSON.stringify(data),
    });
  }

  async put<T>(
    endpoint: string,
    data?: any,
    config?: RequestConfig
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async patch<T>(
    endpoint: string,
    data?: any,
    config?: RequestConfig
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'DELETE',
    });
  }
}

// Export singleton instance
export const apiClient = new ApiClient(API_BASE_URL);

